import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/contexts/LanguageContext";
import api from "@/lib/api";
import { ArrowRight, FileText, BookOpen, Users, Newspaper, Calendar, AlertCircle, CheckCircle2, Activity, Award, TrendingUp, Heart } from "lucide-react";

const HERO_SLIDES = [
  "https://customer-assets.emergentagent.com/job_cupe-oakpark/artifacts/yzepfxtm_file_0000000089007230a67d3f7b8b7d0790.png",
  "https://customer-assets-wrfwihn1.emergentagent.net/job_cupe-oakpark/artifacts/hh8kmxyx_group1%20cupe.jpg",
  "https://customer-assets-wrfwihn1.emergentagent.net/job_cupe-oakpark/artifacts/b7b5n5pg_web1.jpg",
  "https://customer-assets-wrfwihn1.emergentagent.net/job_cupe-oakpark/artifacts/e8mx6fki_web2.jpg",
  "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=1920&q=80",
  "https://images.unsplash.com/photo-1556910103-1c02745aae4d?w=1920&q=80",
  "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=1920&q=80",
];

function Hero() {
  const { t } = useLang();
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setIdx((i) => (i + 1) % HERO_SLIDES.length), 5500);
    return () => clearInterval(id);
  }, []);

  return (
    <section className="relative h-[88vh] min-h-[600px] max-h-[820px] overflow-hidden" data-testid="hero-section">
      {HERO_SLIDES.map((src, i) => (
        <div
          key={src}
          className={`absolute inset-0 transition-opacity duration-[1800ms] ${i === idx ? "opacity-100" : "opacity-0"}`}
          aria-hidden={i !== idx}
        >
          <img src={src} alt="" className="w-full h-full object-cover kenburns" />
        </div>
      ))}
      <div className="absolute inset-0 hero-overlay" />

      <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col justify-center">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur border border-white/30 rounded-full px-3 py-1 text-xs sm:text-sm text-white mb-5">
            <span className="w-2 h-2 rounded-full bg-pink-400 live-dot" /> CUPE Local 4957 · Ottawa
          </div>
          <h1 className="font-display text-white font-bold text-4xl sm:text-5xl lg:text-7xl leading-[1.05] tracking-tight" data-testid="hero-headline">
            {t("hero.headline")}
          </h1>
          <p className="mt-6 text-lg sm:text-xl text-white/90 max-w-2xl leading-relaxed">{t("hero.subtext")}</p>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/rights" className="cupe-gradient text-white px-6 py-3 rounded-md font-semibold text-base min-h-[52px] inline-flex items-center gap-2 hover:opacity-95 transition" data-testid="hero-cta-rights">
              {t("hero.cta1")} <ArrowRight size={18} />
            </Link>
            <Link to="/executive" className="bg-white text-slate-900 px-6 py-3 rounded-md font-semibold text-base min-h-[52px] inline-flex items-center gap-2 hover:bg-pink-50 transition" data-testid="hero-cta-steward">
              {t("hero.cta2")}
            </Link>
            <Link to="/events" className="bg-white/15 backdrop-blur border-2 border-white/50 text-white px-6 py-3 rounded-md font-semibold text-base min-h-[52px] inline-flex items-center gap-2 hover:bg-white/25 transition" data-testid="hero-cta-events">
              {t("hero.cta3")}
            </Link>
          </div>
        </div>

        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2" data-testid="hero-indicators">
          {HERO_SLIDES.map((_, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              className={`h-1.5 rounded-full transition-all ${i === idx ? "w-8 bg-white" : "w-3 bg-white/40"}`}
              aria-label={`Slide ${i + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function QuickActions() {
  const { t } = useLang();
  const cards = [
    { to: "/contact#grievance", icon: FileText, key: "home.quickFile", color: "from-pink-500 to-pink-600" },
    { to: "/resources", icon: BookOpen, key: "home.quickAgreement", color: "from-pink-600 to-rose-600" },
    { to: "/executive", icon: Users, key: "home.quickExec", color: "from-rose-500 to-pink-500" },
    { to: "/member-corner", icon: Heart, key: "home.quickMemberCorner", color: "from-fuchsia-500 to-rose-500" },
    { to: "/news", icon: Newspaper, key: "home.quickNews", color: "from-fuchsia-500 to-pink-600" },
    { to: "/events", icon: Calendar, key: "home.quickEvents", color: "from-pink-500 to-fuchsia-500" },
  ];
  return (
    <section className="py-16 md:py-20 bg-slate-50 border-y border-slate-200" data-testid="quick-actions">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 mb-2">{t("home.quickActions")}</h2>
        <div className="h-1 w-16 cupe-gradient rounded-full mb-10" />
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
          {cards.map((c, i) => (
            <Link
              key={c.key}
              to={c.to}
              data-testid={`quick-card-${i}`}
              className="group bg-white border border-slate-200 rounded-xl p-5 hover:-translate-y-1 hover:shadow-lg transition-all duration-200"
            >
              <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${c.color} flex items-center justify-center mb-4`}>
                <c.icon className="text-white" size={22} />
              </div>
              <div className="font-display font-semibold text-slate-900 group-hover:text-[#d81b7a] transition-colors">{t(c.key)}</div>
              <ArrowRight size={16} className="mt-3 text-slate-400 group-hover:text-[#d81b7a] group-hover:translate-x-1 transition" />
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function CountUp({ to, duration = 1400 }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      setN(Math.floor(p * to));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [to, duration]);
  return <span>{n}</span>;
}

function LeadershipInAction() {
  const { t, lang } = useLang();
  const [stats, setStats] = useState({ grievance_filed: 0, meeting_held: 0, safety_escalation: 0, win_achieved: 0, bargaining_update: 0 });
  const [actions, setActions] = useState([]);

  useEffect(() => {
    api.get("/leadership-stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/leadership-actions").then((r) => setActions(r.data)).catch(() => {});
  }, []);

  const cells = [
    { key: "grievance_filed", labelKey: "home.grievancesFiled", icon: FileText, color: "bg-rose-100 text-rose-600" },
    { key: "meeting_held", labelKey: "home.meetingsHeld", icon: Users, color: "bg-pink-100 text-pink-600" },
    { key: "safety_escalation", labelKey: "home.safetyEscalations", icon: AlertCircle, color: "bg-amber-100 text-amber-700" },
    { key: "win_achieved", labelKey: "home.winsAchieved", icon: Award, color: "bg-emerald-100 text-emerald-700" },
    { key: "bargaining_update", labelKey: "home.bargainingUpdates", icon: TrendingUp, color: "bg-fuchsia-100 text-fuchsia-700" },
  ];

  return (
    <section className="py-16 md:py-24" data-testid="leadership-in-action">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-3 mb-2">
          <span className="w-2.5 h-2.5 rounded-full bg-rose-500 live-dot" />
          <span className="text-xs uppercase tracking-widest font-semibold text-rose-600">Live</span>
        </div>
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900">{t("home.leadershipTitle")}</h2>
        <p className="mt-2 text-slate-600 text-lg max-w-2xl">{t("home.leadershipSub")}</p>

        <div className="mt-10 grid grid-cols-2 md:grid-cols-5 gap-4">
          {cells.map((c) => (
            <div key={c.key} className="bg-white border border-slate-200 rounded-xl p-5" data-testid={`stat-${c.key}`}>
              <div className={`w-10 h-10 rounded-lg ${c.color} flex items-center justify-center mb-3`}>
                <c.icon size={20} />
              </div>
              <div className="font-display text-4xl font-bold text-slate-900"><CountUp to={stats[c.key] || 0} /></div>
              <div className="text-sm text-slate-500 mt-1">{t(c.labelKey)}</div>
            </div>
          ))}
        </div>

        <div className="mt-10 bg-slate-50 border border-slate-200 rounded-xl p-6">
          <div className="font-display font-semibold text-slate-900 mb-4 flex items-center gap-2"><Activity size={18} className="text-[#d81b7a]" /> {t("home.recentActions")}</div>
          <ul className="space-y-3">
            {actions.slice(0, 5).map((a) => (
              <li key={a.id} className="flex items-start gap-3 text-sm">
                <CheckCircle2 size={16} className="mt-0.5 text-[#d81b7a] flex-shrink-0" />
                <div className="flex-1">
                  <div className="text-slate-900">{lang === "fr" ? a.description_fr : a.description_en}</div>
                  <div className="text-xs text-slate-400 mt-0.5">{new Date(a.created_at).toLocaleDateString(lang === "fr" ? "fr-CA" : "en-CA")}</div>
                </div>
              </li>
            ))}
            {actions.length === 0 && <li className="text-sm text-slate-400">No actions yet.</li>}
          </ul>
        </div>
      </div>
    </section>
  );
}

function BargainingPreview() {
  const { t, lang } = useLang();
  const [status, setStatus] = useState({ current_stage: 1, update_en: "", update_fr: "" });
  useEffect(() => { api.get("/bargaining-status").then((r) => setStatus(r.data)).catch(() => {}); }, []);
  const stages = [1, 2, 3, 4, 5];

  return (
    <section className="py-16 md:py-20 bg-gradient-to-br from-pink-50 via-white to-rose-50" data-testid="bargaining-preview">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900">{t("home.bargainingTitle")}</h2>
        <div className="h-1 w-16 cupe-gradient rounded-full mt-3 mb-10" />

        <div className="bg-white border border-slate-200 rounded-2xl p-6 md:p-10">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            {stages.map((n, i) => (
              <React.Fragment key={n}>
                <div className="flex md:flex-col items-center md:items-start gap-3 md:gap-2 flex-1">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-white ${n <= status.current_stage ? "cupe-gradient" : "bg-slate-200 text-slate-500"}`}>
                    {n}
                  </div>
                  <div className={`text-sm font-medium ${n <= status.current_stage ? "text-slate-900" : "text-slate-400"}`}>{t(`bargainingStages.${n}`)}</div>
                </div>
                {i < stages.length - 1 && <div className={`hidden md:block h-1 flex-1 rounded ${n < status.current_stage ? "cupe-gradient" : "bg-slate-200"}`} />}
              </React.Fragment>
            ))}
          </div>
          <div className="bg-slate-50 rounded-xl p-5 border border-slate-100">
            <div className="text-xs uppercase tracking-widest text-[#d81b7a] font-semibold mb-2">{t("bargaining.latestUpdate")}</div>
            <p className="text-slate-700 leading-relaxed">{lang === "fr" ? status.update_fr : status.update_en}</p>
          </div>
          <div className="mt-6">
            <Link to="/bargaining" className="text-[#d81b7a] font-semibold inline-flex items-center gap-1 hover:gap-2 transition-all" data-testid="bargaining-more">
              {t("common.viewAll")} <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function InOurWorkplace() {
  const { t } = useLang();
  const tiles = [
    { src: "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=1200&q=80", labelKey: "home.wpBoard" },
    { src: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=1200&q=80", labelKey: "home.wpCare" },
    { src: "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=1200&q=80", labelKey: "home.wpSolidarity" },
    { src: "https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?w=1200&q=80", labelKey: "home.wpFrontline" },
    { src: "https://images.unsplash.com/photo-1551601651-2a8555f1a136?w=1200&q=80", labelKey: "home.wpElderly" },
    { src: "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=1200&q=80", labelKey: "home.wpTraining" },
  ];
  return (
    <section className="py-16 md:py-20 bg-white" data-testid="in-our-workplace">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900">{t("home.wpTitle")}</h2>
        <p className="mt-2 text-slate-600 text-lg max-w-2xl">{t("home.wpSub")}</p>
        <div className="h-1 w-16 cupe-gradient rounded-full mt-3 mb-10" />
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
          {tiles.map((tile, i) => (
            <figure
              key={i}
              className={`relative overflow-hidden rounded-2xl border border-slate-200 group ${i === 0 ? "md:col-span-2 md:row-span-2 aspect-square md:aspect-auto" : "aspect-square"}`}
              data-testid={`wp-tile-${i}`}
            >
              <img src={tile.src} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/85 via-slate-900/10 to-transparent" />
              <figcaption className="absolute bottom-0 left-0 right-0 p-4 text-white font-display font-semibold text-base md:text-lg">
                {t(tile.labelKey)}
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

function NewsAndEvents() {
  const { t, lang } = useLang();
  const [news, setNews] = useState([]);
  const [events, setEvents] = useState([]);
  useEffect(() => {
    api.get("/news").then((r) => setNews(r.data)).catch(() => {});
    api.get("/events").then((r) => setEvents(r.data)).catch(() => {});
  }, []);
  const upcoming = events.filter((e) => new Date(e.start_at) > new Date()).slice(0, 3);

  return (
    <section className="py-16 md:py-20" data-testid="news-events">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-10">
        <div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 mb-2">{t("home.newsTitle")}</h2>
          <div className="h-1 w-12 cupe-gradient rounded-full mb-6" />
          <div className="space-y-4">
            {news.slice(0, 3).map((p) => (
              <Link key={p.id} to={`/news`} className="block bg-white border border-slate-200 rounded-xl p-5 hover:border-[#d81b7a] transition-colors" data-testid="news-card">
                <div className="text-xs uppercase tracking-widest text-[#d81b7a] font-semibold mb-2">{p.category}</div>
                <div className="font-display font-semibold text-slate-900 text-lg leading-snug">{lang === "fr" ? p.title_fr : p.title_en}</div>
                <p className="text-sm text-slate-600 mt-2 line-clamp-2">{lang === "fr" ? p.content_fr : p.content_en}</p>
              </Link>
            ))}
            {news.length === 0 && <p className="text-slate-400 text-sm">{t("home.noNews")}</p>}
          </div>
          <Link to="/news" className="mt-6 inline-flex items-center gap-1 text-[#d81b7a] font-semibold hover:gap-2 transition-all">
            {t("common.viewAll")} <ArrowRight size={16} />
          </Link>
        </div>

        <div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 mb-2">{t("home.eventsTitle")}</h2>
          <div className="h-1 w-12 cupe-gradient rounded-full mb-6" />
          <div className="space-y-4">
            {upcoming.map((e) => {
              const d = new Date(e.start_at);
              return (
                <div key={e.id} className="bg-white border border-slate-200 rounded-xl p-5 flex gap-4" data-testid="event-card">
                  <div className="text-center cupe-gradient text-white rounded-lg p-3 w-16 flex-shrink-0">
                    <div className="text-xs uppercase">{d.toLocaleString(lang === "fr" ? "fr-CA" : "en-CA", { month: "short" })}</div>
                    <div className="font-display font-bold text-2xl leading-none mt-1">{d.getDate()}</div>
                  </div>
                  <div>
                    <div className="font-display font-semibold text-slate-900">{lang === "fr" ? e.title_fr : e.title_en}</div>
                    <div className="text-sm text-slate-500 mt-1">{d.toLocaleString(lang === "fr" ? "fr-CA" : "en-CA", { hour: "2-digit", minute: "2-digit" })} · {e.location}</div>
                  </div>
                </div>
              );
            })}
            {upcoming.length === 0 && <p className="text-slate-400 text-sm">{t("home.noEvents")}</p>}
          </div>
          <Link to="/events" className="mt-6 inline-flex items-center gap-1 text-[#d81b7a] font-semibold hover:gap-2 transition-all">
            {t("common.viewAll")} <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <QuickActions />
      <LeadershipInAction />
      <BargainingPreview />
      <NewsAndEvents />
    </div>
  );
}
